import { useUpdateSubjectDID } from "@/src/components/templates/EditSubjectDID/context/UpdateSubjectDID";
import { useEffect, useState } from "react";
import FormControl from "@mui/material/FormControl";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    GetPaginatedSubjectDidsDocument,
    useGetSubjectDidQuery,
    useUpdateSubjectDidMutation,
} from "@/src/graphql/generated/api/graphql";
import { useNavigate } from "@tanstack/react-router";
import { editSubjectDIDRoute } from "@/src/providers/router/router";
import { useWatch } from "react-hook-form";
import { styled } from "../shared/Template.styles";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";

const EditSubjectDIDTemplate = () => {
    const { t } = useTranslation("common", {
        keyPrefix: "subject-did-form",
    });

    const { id } = editSubjectDIDRoute.useParams();

    const { subjectDid } = useSubjectDid(id);
    const updateSubjectDID = useUpdateSubjectDID();
    const [loading, setLoading] = useState(false);

    const { form } = updateSubjectDID;

    const subjectDidForm = useWatch({
        control: form.control,
        name: "subjectDid",
    });

    useEffect(() => {
        if (subjectDid) {
            form.reset({
                subjectDid: {
                    iri: subjectDid?.healthcareOfficeIri || "",
                },
            });
        }
    }, [subjectDid]);

    const navigate = useNavigate();

    const onSubmit = form.handleSubmit(
        () => {
            setLoading(true);
            updateSubjectDidMutation();
        },
        (error) => {
            console.error(error);
        }
    );

    const [updateSubjectDidMutation] = useUpdateSubjectDidMutation({
        variables: {
            input: {
                id: id,
                healthcareOfficeIri: subjectDidForm?.iri,
            },
        },
        refetchQueries: [GetPaginatedSubjectDidsDocument],
        onCompleted: (_data) => {
            setLoading(false);
            void navigate({ to: "/admin/subject-dids" });
        },
        onError: (error) => {
            console.error(error);
            setLoading(false);
        },
    });

    return (
        <>
            <styled.Container>
                <styled.Header
                    title={
                        subjectDid?.name && subjectDid?.zinlDid
                            ? `${subjectDid.name} - ${subjectDid.zinlDid}`
                            : t("not-found")
                    }
                />
                {subjectDid ? (
                    <styled.Main>
                        <form onSubmit={onSubmit} data-testid="form">
                            <Stack spacing={4} direction={"column"}>
                                <FormControl>
                                    <TextField
                                        id="iri"
                                        defaultValue={
                                            subjectDid?.healthcareOfficeIri ??
                                            t("zinIri")
                                        }
                                        label={t("zinIri")}
                                        {...form.register("subjectDid.iri")}
                                    />
                                </FormControl>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        mt: 2,
                                    }}
                                >
                                    <LoadingButton
                                        type="submit"
                                        loading={loading}
                                        variant="contained"
                                        color="primary"
                                        disabled={!form.formState.isValid}
                                        data-testid="submit-button"
                                    >
                                        {t("update")}
                                    </LoadingButton>
                                </Box>
                            </Stack>
                        </form>
                    </styled.Main>
                ) : null}
            </styled.Container>
        </>
    );
};

function useSubjectDid(id: string | undefined) {
    const { loading, data, refetch } = useGetSubjectDidQuery({
        variables: {
            id: id,
        },
        fetchPolicy: "network-only",
    });

    return {
        loading,
        refetch,
        subjectDid: data?.getSubjectDid,
    };
}

export default EditSubjectDIDTemplate;
